document.documentElement.classList.remove('no-js');

const clamp = (value) => Math.max(0, Math.min(1, value));
const ease = (value) => value * value * (3 - 2 * value);
const rootUrl = () => (window.Shopify?.routes?.root || '/');
let activeScrollUpdate = null;

function sectionProgress(section) {
  const rect = section.getBoundingClientRect();
  return clamp(-rect.top / Math.max(1, section.offsetHeight - window.innerHeight));
}

function initHeader() {
  const header = document.querySelector('[data-header]');
  const toggle = document.querySelector('[data-mega-toggle]');
  if (header && toggle && !header.dataset.ssReady) {
    header.dataset.ssReady = 'true';
    const open = () => { header.classList.add('is-expanded'); toggle.setAttribute('aria-expanded', 'true'); };
    const close = () => { header.classList.remove('is-expanded'); toggle.setAttribute('aria-expanded', 'false'); };
    toggle.addEventListener('mouseenter', open);
    toggle.addEventListener('click', () => header.classList.contains('is-expanded') ? close() : open());
    header.addEventListener('mouseleave', close);
    toggle.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
  }
  const menu = document.querySelector('[data-mobile-menu]');
  const menuOpen = document.querySelector('[data-mobile-toggle]');
  const menuClose = document.querySelector('[data-mobile-close]');
  const setMobile = (active) => { if (!menu) return; menu.classList.toggle('is-open', active); menu.setAttribute('aria-hidden', String(!active)); document.body.style.overflow = active ? 'hidden' : ''; };
  if (menu && !menu.dataset.ssReady) {
    menu.dataset.ssReady = 'true';
    menuOpen?.addEventListener('click', () => setMobile(true));
    menuClose?.addEventListener('click', () => setMobile(false));
  }
}

function initHeroTilt() {
  if (!window.matchMedia('(pointer: fine)').matches) return;
  document.querySelectorAll('[data-sweep-stage-root]').forEach((stage) => {
    if (stage.dataset.ssTiltReady) return;
    stage.dataset.ssTiltReady = 'true';
    const heading = stage.querySelector('[data-hero-tilt]');
    if (!heading) return;
    let frame = 0;
    let pointerX = 0;
    let pointerY = 0;
    const paint = () => {
      frame = 0;
      const rect = stage.getBoundingClientRect();
      const x = (pointerX - rect.left) / Math.max(rect.width, 1) - .5;
      const y = (pointerY - rect.top) / Math.max(rect.height, 1) - .5;
      heading.style.setProperty('--tilt-x', `${(-y * 5).toFixed(2)}deg`);
      heading.style.setProperty('--tilt-y', `${(x * 7).toFixed(2)}deg`);
      heading.style.setProperty('--follow-x', `${(x * 18).toFixed(1)}px`);
      heading.style.setProperty('--follow-y', `${(y * 12).toFixed(1)}px`);
    };
    stage.addEventListener('pointermove', (event) => {
      pointerX = event.clientX;
      pointerY = event.clientY;
      if (!frame) frame = requestAnimationFrame(paint);
    });
    stage.addEventListener('pointerleave', () => {
      cancelAnimationFrame(frame);
      frame = 0;
      heading.style.setProperty('--tilt-x', '0deg');
      heading.style.setProperty('--tilt-y', '0deg');
      heading.style.setProperty('--follow-x', '0px');
      heading.style.setProperty('--follow-y', '0px');
    });
  });
}

function initDepthHeadings() {
  if (!window.matchMedia('(pointer: fine)').matches) return;
  document.querySelectorAll('[data-depth-heading]').forEach((heading) => {
    if (heading.dataset.ssReady) return;
    heading.dataset.ssReady = 'true';
    const root = heading.closest('.ss-rebuilt-store__copy') || heading;
    let frame = 0;
    let pointerX = 0;
    let pointerY = 0;
    const paint = () => {
      frame = 0;
      const rect = heading.getBoundingClientRect();
      const x = clamp((pointerX - rect.left) / Math.max(rect.width, 1)) - .5;
      const y = clamp((pointerY - rect.top) / Math.max(rect.height, 1)) - .5;
      heading.style.setProperty('--depth-x', `${(x * 13).toFixed(1)}px`);
      heading.style.setProperty('--depth-y', `${(y * 8).toFixed(1)}px`);
      heading.style.setProperty('--depth-rx', `${(-y * 2.5).toFixed(2)}deg`);
      heading.style.setProperty('--depth-ry', `${(x * 3.5).toFixed(2)}deg`);
    };
    root.addEventListener('pointermove', (event) => {
      pointerX = event.clientX;
      pointerY = event.clientY;
      if (!frame) frame = requestAnimationFrame(paint);
    });
    root.addEventListener('pointerleave', () => {
      cancelAnimationFrame(frame);
      frame = 0;
      heading.style.setProperty('--depth-x', '0px');
      heading.style.setProperty('--depth-y', '0px');
      heading.style.setProperty('--depth-rx', '0deg');
      heading.style.setProperty('--depth-ry', '0deg');
    });
  });
}

function formatMetric(value, kind) {
  if (kind === 'money') return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR', maximumFractionDigits: 0 }).format(value);
  if (kind === 'percent') return `${value.toFixed(2)}%`;
  return new Intl.NumberFormat(document.documentElement.lang || 'en').format(Math.round(value));
}

function initScrollStories() {
  if (activeScrollUpdate) {
    window.removeEventListener('scroll', activeScrollUpdate);
    window.removeEventListener('resize', activeScrollUpdate);
  }
  const sweep = document.querySelector('[data-ss-scroll="sweep"]');
  const metrics = document.querySelector('[data-ss-scroll="metrics"]');
  const conversion = document.querySelector('[data-ss-scroll="conversion"]');
  const timeline = document.querySelector('[data-ss-scroll="timeline"]');
  let frame = 0;

  const update = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      if (sweep) {
        const progress = sectionProgress(sweep);
        const intro = 1 - ease(clamp((progress - .06) / .11));
        const problem = ease(clamp((progress - .13) / .08)) * (1 - ease(clamp((progress - .3) / .07)));
        const storeFocus = ease(clamp((progress - .12) / .1)) * (1 - ease(clamp((progress - .5) / .12)));
        const bridge = ease(clamp((progress - .4) / .07)) * (1 - ease(clamp((progress - .51) / .08)));
        const scan = ease(clamp((progress - .55) / .2));
        const breakApart = ease(clamp((progress - .59) / .22));
        const rebuild = ease(clamp((progress - .72) / .25));
        sweep.style.setProperty('--intro', intro);
        sweep.style.setProperty('--problem', problem);
        sweep.style.setProperty('--store-focus', storeFocus);
        sweep.style.setProperty('--bridge', bridge);
        sweep.style.setProperty('--scan', scan);
        sweep.style.setProperty('--scan-pos', `${10 + scan * 90}%`);
        sweep.style.setProperty('--scan-opacity', scan * (1 - rebuild));
        sweep.style.setProperty('--break', breakApart);
        sweep.style.setProperty('--rebuild', rebuild);
        const status = sweep.querySelector('[data-sweep-status]');
        const percent = sweep.querySelector('[data-sweep-percent]');
        if (status) status.textContent = progress < .13 ? 'SCROLL TO BEGIN' : progress < .4 ? 'DEFAULT STOREFRONT DETECTED' : progress < .55 ? 'READY TO SWEEP' : progress < .76 ? 'SWEEPING THE DEFAULT' : progress < .96 ? 'REBUILDING THE STOREFRONT' : 'SITESWEEP UNLOCKED';
        if (percent) percent.textContent = `${String(Math.round(progress * 100)).padStart(2, '0')}%`;
      }
      if (metrics) {
        const progress = sectionProgress(metrics);
        const movement = ease(clamp((progress - .12) / .74));
        const admin = metrics.querySelector('[data-admin]');
        admin?.style.setProperty('--metric-progress', movement);
        admin?.style.setProperty('--chart-dash', Math.max(0, .56 - movement * .56));
        metrics.querySelectorAll('[data-metric]').forEach((metric) => {
          const before = Number(metric.dataset.before || 0);
          const after = Number(metric.dataset.after || before);
          metric.textContent = formatMetric(before + (after - before) * movement, metric.dataset.kind);
        });
        metrics.querySelectorAll('[data-delta]').forEach((delta) => {
          const positive = movement > .38;
          delta.textContent = positive ? delta.dataset.after : delta.dataset.before;
          delta.classList.toggle('is-positive', positive);
        });
        const chartTotal = metrics.querySelector('[data-chart-total]');
        if (chartTotal) chartTotal.textContent = formatMetric(1284 + (42817 - 1284) * movement, 'money');
        const status = metrics.querySelector('[data-metrics-status]');
        const percent = metrics.querySelector('[data-metrics-percent]');
        if (status) status.textContent = progress < .12 ? 'SWEEP OUTSTANDING' : progress < .86 ? 'STOREFRONT OPTIMIZING' : 'CONVERSION LAYER ACTIVE';
        if (percent) percent.textContent = `${Math.round(ease(progress) * 100)}%`;
      }
      if (conversion) updateConversion(conversion, sectionProgress(conversion));
      if (timeline) updateTimeline(timeline, sectionProgress(timeline));
      const pageProgress = document.querySelector('[data-page-progress]');
      if (pageProgress) {
        const distance = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
        pageProgress.style.transform = `scaleX(${clamp(window.scrollY / distance)})`;
      }
    });
  };
  update();
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  activeScrollUpdate = update;
}

function parseData(section, selector) {
  try { return JSON.parse(section.querySelector(selector)?.textContent || '[]'); } catch { return []; }
}

function updateConversion(section, progress) {
  const data = parseData(section, '[data-conversion-data]');
  if (!data.length) return;
  const active = Math.min(data.length - 1, Math.floor(progress * data.length));
  section.style.setProperty('--conversion-progress', progress);
  section.querySelectorAll('[data-conversion-point]').forEach((point, index) => point.classList.toggle('is-active', index <= active));
  const title = section.querySelector('[data-conversion-title]');
  const copy = section.querySelector('[data-conversion-copy]');
  const count = section.querySelector('[data-conversion-count]');
  const bar = section.querySelector('[data-conversion-bar]');
  if (title) title.textContent = data[active].title;
  if (copy) copy.textContent = data[active].copy;
  if (count) count.textContent = `${String(active + 1).padStart(2, '0')} / ${String(data.length).padStart(2, '0')}`;
  if (bar) bar.style.width = `${((active + 1) / data.length) * 100}%`;
}

function updateTimeline(section, progress) {
  const data = parseData(section, '[data-route-data]');
  if (!data.length) return;
  const active = Math.min(data.length - 1, Math.floor(progress * data.length));
  section.style.setProperty('--route-progress', progress);
  section.style.setProperty('--route-position', `${5 + progress * 90}%`);
  section.style.setProperty('--route-fill', `${progress * 100}%`);
  section.querySelectorAll('[data-route-stop]').forEach((stop, index) => stop.classList.toggle('is-active', index <= active));
  const title = section.querySelector('[data-route-title]');
  const copy = section.querySelector('[data-route-copy]');
  const count = section.querySelector('[data-route-count]');
  if (title) title.textContent = data[active].title;
  if (copy) copy.textContent = data[active].copy;
  if (count) count.textContent = `STEP ${String(active + 1).padStart(2, '0')}`;
}

function initCounters() {
  const counters = document.querySelectorAll('[data-count-up]');
  if (!counters.length) return;
  const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    const node = entry.target;
    const target = Number(node.dataset.countUp || 0);
    const suffix = node.textContent.replace(/[0-9]/g, '');
    const start = performance.now();
    const run = (now) => { const progress = Math.min(1, (now - start) / 1100); node.textContent = `${Math.round(target * (1 - Math.pow(1 - progress, 3)))}${suffix}`; if (progress < 1) requestAnimationFrame(run); };
    requestAnimationFrame(run);
    observer.unobserve(node);
  }), { threshold: .5 });
  counters.forEach((counter) => observer.observe(counter));
}

function initFaq() {
  document.querySelectorAll('[data-faq-list]').forEach((list) => {
    if (list.dataset.ssReady) return;
    list.dataset.ssReady = 'true';
    list.querySelectorAll('details').forEach((item) => item.addEventListener('toggle', () => {
      if (!item.open) return;
      list.querySelectorAll('details[open]').forEach((other) => { if (other !== item) other.open = false; });
    }));
  });
}

async function getCart() {
  const response = await fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('Cart could not be loaded');
  return response.json();
}

function money(cents) {
  return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' }).format(cents / 100);
}

function renderCart(cart) {
  document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
  const body = document.querySelector('[data-cart-body]');
  const total = document.querySelector('[data-cart-total]');
  if (total) total.textContent = money(cart.total_price);
  if (!body) return;
  if (!cart.items.length) { body.innerHTML = '<div class="ss-cart-empty"><span>0</span><h3>YOUR CART IS CLEAN.</h3><p>Add a theme built for the next version of your brand.</p></div>'; return; }
  body.innerHTML = cart.items.map((item) => `<article>${item.image ? `<a href="${item.url}"><img src="${item.image}" alt=""></a>` : '<span class="ss-cart-drawer__placeholder">SS</span>'}<div><small>${item.product_type || 'Shopify theme'}</small><strong>${item.product_title}</strong><span>${money(item.final_price)}</span><div class="ss-cart-quantity"><button type="button" data-cart-change="${item.key}" data-cart-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button><b>${item.quantity}</b><button type="button" data-cart-change="${item.key}" data-cart-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button></div><button type="button" data-cart-remove="${item.key}">REMOVE</button></div></article>`).join('');
}

function initCart() {
  const drawer = document.querySelector('[data-cart-drawer]');
  const drawerReady = drawer?.dataset.ssReady === 'true';
  if (drawer && !drawerReady) drawer.dataset.ssReady = 'true';
  const open = async () => { if (!drawer) return; try { renderCart(await getCart()); } catch {} drawer.classList.add('is-open'); drawer.setAttribute('aria-hidden', 'false'); document.body.style.overflow = 'hidden'; };
  const close = () => { if (!drawer) return; drawer.classList.remove('is-open'); drawer.setAttribute('aria-hidden', 'true'); document.body.style.overflow = ''; };
  if (!drawerReady) document.querySelectorAll('[data-cart-open]').forEach((button) => button.addEventListener('click', open));
  if (!drawerReady) document.querySelectorAll('[data-cart-close]').forEach((button) => button.addEventListener('click', close));
  if (!drawerReady) drawer?.querySelector('[data-cart-body]')?.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cart-remove], [data-cart-change]');
    if (!button) return;
    button.disabled = true;
    try {
      const id = button.dataset.cartRemove || button.dataset.cartChange;
      const quantity = button.dataset.cartRemove ? 0 : Number(button.dataset.cartQuantity || 0);
      const response = await fetch(`${rootUrl()}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id, quantity }) });
      if (!response.ok) throw new Error('Could not update cart');
      renderCart(await response.json());
    } catch { button.disabled = false; }
  });
  document.querySelectorAll('.ss-product-form').forEach((form) => {
    if (form.dataset.ssReady) return;
    form.dataset.ssReady = 'true';
    form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const button = form.querySelector('[data-add-button]');
    if (button?.disabled) return;
    button?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${rootUrl()}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      if (!response.ok) throw new Error('Could not add this theme');
      renderCart(await getCart());
      if (window.innerWidth > 0) open();
    } catch (error) { window.alert(error.message); }
    finally { button?.removeAttribute('aria-busy'); }
    });
  });
}

function initProduct() {
  document.querySelectorAll('[data-product-root]').forEach((root) => {
    if (root.dataset.ssReady) return;
    root.dataset.ssReady = 'true';
    const variants = parseData(root, '[data-product-json]');
    const optionInputs = root.querySelectorAll('[data-option-value]');
    const variantInput = root.querySelector('[data-variant-id]');
    const addButton = root.querySelector('[data-add-button]');
    const addText = root.querySelector('[data-add-text]');
    const updateVariant = () => {
      const options = [...root.querySelectorAll('[data-option-value]:checked')].map((input) => input.value);
      const variant = variants.find((item) => item.options.every((option, index) => option === options[index]));
      if (!variant || !variantInput) return;
      variantInput.value = variant.id;
      if (addButton) addButton.disabled = !variant.available;
      if (addText) addText.textContent = variant.available ? `GET THIS THEME NOW` : 'SOLD OUT';
      const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); window.history.replaceState({}, '', url);
    };
    optionInputs.forEach((input) => input.addEventListener('change', updateVariant));
    root.querySelector('[data-qty-minus]')?.addEventListener('click', () => { const input = root.querySelector('[data-qty]'); input.value = Math.max(1, Number(input.value) - 1); });
    root.querySelector('[data-qty-plus]')?.addEventListener('click', () => { const input = root.querySelector('[data-qty]'); input.value = Number(input.value) + 1; });
    root.querySelectorAll('[data-media-target]').forEach((button) => button.addEventListener('click', () => {
      root.querySelectorAll('[data-media-target]').forEach((item) => item.classList.toggle('is-active', item === button));
      root.querySelectorAll('[data-media-id]').forEach((media) => media.classList.toggle('is-active', media.dataset.mediaId === button.dataset.mediaTarget));
    }));
  });
}

function initThemeBoxes() {
  document.querySelectorAll('[data-theme-rotatable]').forEach((box) => {
    if (box.dataset.ssReady) return;
    box.dataset.ssReady = 'true';
    let rotationX = -3;
    let rotationY = -18;
    let startX = 0;
    let startY = 0;
    let baseX = 0;
    let baseY = 0;
    let dragging = false;
    let frame = 0;
    const draw = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        box.style.setProperty('--box-rx', `${rotationX.toFixed(2)}deg`);
        box.style.setProperty('--box-ry', `${rotationY.toFixed(2)}deg`);
      });
    };
    const reset = () => { rotationX = -3; rotationY = -18; draw(); };
    box.addEventListener('pointerdown', (event) => {
      dragging = true;
      startX = event.clientX;
      startY = event.clientY;
      baseX = rotationX;
      baseY = rotationY;
      box.classList.add('is-dragging');
      box.setPointerCapture?.(event.pointerId);
    });
    box.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      rotationY = baseY + (event.clientX - startX) * .22;
      rotationX = Math.max(-14, Math.min(14, baseX - (event.clientY - startY) * .12));
      draw();
    });
    const stop = (event) => {
      if (!dragging) return;
      dragging = false;
      box.classList.remove('is-dragging');
      box.releasePointerCapture?.(event.pointerId);
    };
    box.addEventListener('pointerup', stop);
    box.addEventListener('pointercancel', stop);
    box.addEventListener('dblclick', reset);
    box.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') return reset();
      if (event.key === 'ArrowLeft') rotationY -= 12;
      if (event.key === 'ArrowRight') rotationY += 12;
      if (event.key === 'ArrowUp') rotationX = Math.max(-18, rotationX - 5);
      if (event.key === 'ArrowDown') rotationX = Math.min(18, rotationX + 5);
      draw();
    });
    draw();
  });
}

function initRevealMoments() {
  document.querySelectorAll('[data-team-reveal]').forEach((section) => {
    if (section.dataset.ssReady) return;
    section.dataset.ssReady = 'true';
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      section.classList.add('is-visible');
      observer.disconnect();
    }, { threshold: .28 });
    observer.observe(section);
  });
}

function initComparison() {
  document.querySelectorAll('[data-theme-comparison]').forEach((section) => {
    if (section.dataset.ssReady) return;
    section.dataset.ssReady = 'true';
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      section.classList.add('is-visible');
      observer.disconnect();
    }, { threshold: .12 });
    observer.observe(section);
  });
}

function initSort() {
  document.querySelectorAll('[data-sort-select]').forEach((select) => {
    if (select.dataset.ssReady) return;
    select.dataset.ssReady = 'true';
    select.addEventListener('change', () => { const url = new URL(window.location.href); url.searchParams.set('sort_by', select.value); window.location.assign(url.toString()); });
  });
}

function init() { initHeader(); initHeroTilt(); initDepthHeadings(); initScrollStories(); initCounters(); initFaq(); initCart(); initProduct(); initThemeBoxes(); initRevealMoments(); initComparison(); initSort(); }
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
document.addEventListener('shopify:section:load', init);
